import React, { useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, Network, Handshake, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { LOAD_BOARDS, BROKERAGES } from '../data/partners';

const PartnersPage = () => {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('brokers'); // brokers | loadboards

  const filteredBrokers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BROKERAGES;
    return BROKERAGES.filter(b => b.toLowerCase().includes(q));
  }, [query]);

  const filteredBoards = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return LOAD_BOARDS;
    return LOAD_BOARDS.filter(b => b.toLowerCase().includes(q));
  }, [query]);

  // Group brokerages by first letter
  const grouped = useMemo(() => {
    const g = {};
    filteredBrokers.forEach(name => {
      const letter = /^[A-Za-z]/.test(name) ? name[0].toUpperCase() : '#';
      if (!g[letter]) g[letter] = [];
      g[letter].push(name);
    });
    return g;
  }, [filteredBrokers]);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0A1628] text-white">
          <div className="absolute inset-0 z-0 brand-gradient" />
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#D4A24C]/10 blur-3xl" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#D4A24C]">Our network</span>
            <h1 className="mt-4 text-4xl md:text-6xl font-extrabold leading-[1.05] max-w-3xl">
              Load boards & brokerage partners.
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-white/80">
              A transparent look at the platforms we operate on and the brokerage companies we’ve partnered with over the years.
            </p>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl">
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm">
                <div className="text-3xl font-extrabold text-[#D4A24C]">{LOAD_BOARDS.length}</div>
                <div className="text-sm font-semibold mt-1">Load boards</div>
                <div className="text-xs text-white/60">Connected in real time</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm">
                <div className="text-3xl font-extrabold text-[#D4A24C]">{BROKERAGES.length}+</div>
                <div className="text-sm font-semibold mt-1">Brokerage partners</div>
                <div className="text-xs text-white/60">Nationwide relationships</div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm">
                <div className="text-3xl font-extrabold text-[#D4A24C]">24/7</div>
                <div className="text-sm font-semibold mt-1">Dispatch coverage</div>
                <div className="text-xs text-white/60">Always-on operations</div>
              </div>
            </div>
          </div>
        </section>

        {/* Search + Tabs */}
        <section className="bg-white border-b border-[#E5EAF2] sticky top-16 md:top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setTab('brokers')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 ${tab === 'brokers' ? 'bg-[#0A1628] text-white' : 'bg-[#F7F9FC] text-[#0A1628] hover:bg-[#ECF1F8]'}`}
              >
                <Handshake className="w-4 h-4" />
                Brokerage partners
                <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${tab === 'brokers' ? 'bg-[#D4A24C] text-[#0A1628]' : 'bg-[#0A1628]/10'}`}>{BROKERAGES.length}</span>
              </button>
              <button
                onClick={() => setTab('loadboards')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors flex items-center gap-2 ${tab === 'loadboards' ? 'bg-[#0A1628] text-white' : 'bg-[#F7F9FC] text-[#0A1628] hover:bg-[#ECF1F8]'}`}
              >
                <Network className="w-4 h-4" />
                Load boards
                <span className={`ml-1 text-[11px] px-1.5 py-0.5 rounded ${tab === 'loadboards' ? 'bg-[#D4A24C] text-[#0A1628]' : 'bg-[#0A1628]/10'}`}>{LOAD_BOARDS.length}</span>
              </button>
            </div>
            <div className="relative md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
              <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search partners…" className="pl-9 h-10" />
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="section bg-[#F7F9FC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {tab === 'loadboards' && (
              <>
                <h2 className="text-2xl md:text-3xl font-extrabold navy-text mb-6">Connected load boards</h2>
                {filteredBoards.length === 0 ? (
                  <div className="text-center py-20 text-[#4A5A72]">No load boards match “{query}”</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {filteredBoards.map(name => (
                      <div key={name} className="card-lift group flex items-center gap-3 px-4 py-4 rounded-xl border border-[#E5EAF2] bg-white">
                        <CheckCircle2 className="w-5 h-5 text-[#D4A24C] shrink-0" />
                        <span className="font-bold text-sm text-[#0A1628] truncate group-hover:gold-text transition-colors">{name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === 'brokers' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl md:text-3xl font-extrabold navy-text">Brokerage partners</h2>
                  <span className="text-sm text-[#4A5A72]">Showing <strong>{filteredBrokers.length}</strong> of {BROKERAGES.length}</span>
                </div>

                {filteredBrokers.length === 0 ? (
                  <div className="text-center py-20 text-[#4A5A72]">No brokers match “{query}”</div>
                ) : (
                  <div className="space-y-8">
                    {Object.keys(grouped).sort().map(letter => (
                      <div key={letter}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-[#0A1628] text-[#D4A24C] font-extrabold flex items-center justify-center text-sm">{letter}</div>
                          <div className="flex-1 h-px bg-[#E5EAF2]" />
                          <span className="text-xs font-semibold text-[#4A5A72]">{grouped[letter].length}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {grouped[letter].map(name => (
                            <div key={name} className="px-4 py-3 rounded-lg bg-white border border-[#E5EAF2] text-sm font-semibold text-[#0A1628] truncate hover:border-[#D4A24C] hover:shadow-sm transition-all" title={name}>
                              {name}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* CTA */}
            <div className="mt-16 bg-[#0A1628] text-white rounded-3xl p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-extrabold">Want to become a partner?</h3>
                <p className="text-white/70 mt-2">Let’s connect — we’re always onboarding new brokers and carriers.</p>
              </div>
              <a href="/contact"><Button className="btn-accent h-12 px-8 font-bold">Get in touch</Button></a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default PartnersPage;
